import {
  buildBookingDataFromVehicle,
  fetchPublicVehicles,
  groupVehiclesByTrip,
  toBookingTripLabel,
} from '../../services/vehicleService';

const parseMoney = (value) => {
  const amount = Number(String(value || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(amount) ? amount : 0;
};

const firstSeatLabel = (vehicle = {}) => {
  const name = String(vehicle.name || vehicle.vehicleType || '');
  const match = name.match(/\d+\+1|\d+/);
  if (match) return `${match[0]} Seater`;
  return `${vehicle.seaters || vehicle.seats || 4} Seater`;
};

const titleCase = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const matchesAny = (vehicle, keywords) => {
  const value = `${vehicle.name || ''} ${vehicle.vehicleType || ''}`.toLowerCase();
  return keywords.some((keyword) => value.includes(keyword));
};

const getVehicleBackendId = (vehicle = {}) => {
  const candidates = [
    vehicle.backendVehicleId,
    vehicle.vehicleId,
    vehicle.id,
    vehicle.apiVehicle?.backendVehicleId,
    vehicle.apiVehicle?.vehicleId,
    vehicle.apiVehicle?.id,
  ];

  for (const candidate of candidates) {
    const id = Number(candidate);

    if (Number.isInteger(id) && id > 0) return id;
  }

  return null;
};

const vehicleGroupKey = (vehicle = {}) =>
  [
    String(vehicle.vehicleType || vehicle.name || '').trim().toLowerCase(),
    String(vehicle.vehicleCategory || vehicle.category || '').trim().toLowerCase(),
    String(vehicle.capacity || vehicle.seats || vehicle.seaters || ''),
  ].join('|');

const vehicleScore = (vehicle = {}) => {
  const rateCard = vehicle.selectedRateCard || vehicle.rateCard || {};
  let score = 0;

  if (rateCard.baseRate || rateCard.rate) score += 100;
  if (vehicle.price && vehicle.price !== 'Fare on request') score += 50;
  if (vehicle.registrationNumber) score += 10;
  if (getVehicleBackendId(vehicle)) score += 5;

  return score;
};

const dedupeVehicles = (vehicles = []) => {
  const grouped = new Map();

  vehicles.forEach((vehicle) => {
    const key = vehicleGroupKey(vehicle);
    const existing = grouped.get(key);

    if (!existing || vehicleScore(vehicle) > vehicleScore(existing)) {
      grouped.set(key, vehicle);
    }
  });

  return Array.from(grouped.values());
};

const tripData = (vehicle, trip) => {
  const details = vehicle.packageDetails || {};
  const price = parseMoney(details.baseRate ?? vehicle.price);

  if (trip === 'local') {
    return {
      price,
      baseKm: details.kms || 80,
      baseHours: details.hours || 8,
      extraKm: details.extraKm || 0,
      extraHour: details.extraHr || 0,
      priceLabel: 'per day',
      includes: `${details.hours || 8} hours | ${details.kms || 80} km`,
    };
  }

  if (trip === 'outstation') {
    return {
      price,
      pricePerKm: price,
      minKms: details.kms || 300,
      driverBeta: details.driverBata || 0,
      priceLabel: 'per km',
      includes: `${details.kms || 300} km/day included`,
    };
  }

  return {
    price,
    priceLabel: 'airport transfer',
    includes: 'Airport pickup/drop',
    bags: vehicle.bags,
  };
};

const toLandingVehicle = (vehicle, trip, imageMap = {}) => {
  const details = tripData(vehicle, trip);
  const image = imageMap[vehicle.image] || imageMap.default;
  const seatLabel = firstSeatLabel(vehicle);
  const cleanName = vehicle.name || vehicle.vehicleType || 'Vehicle';
  const vehicleBackendId = getVehicleBackendId(vehicle);

  return {
    ...details,
    apiVehicle: vehicle,
    id: vehicleBackendId || `${trip}-${vehicle.id || cleanName}`,
    ...(vehicleBackendId
      ? {
          vehicleId: vehicleBackendId,
          backendVehicleId: vehicleBackendId,
        }
      : {}),
    name: titleCase(cleanName),
    variant: seatLabel,
    models: vehicle.models,
    type: vehicle.type,
    seats: seatLabel,
    fuel: vehicle.fuelType || 'Diesel',
    transmission: 'Manual',
    ac: 'AC',
    luggage: `${vehicle.bags || 4} bags`,
    bags: vehicle.bags || details.bags || 4,
    image,
    features: vehicle.features,
    rating: vehicle.rating,
  };
};

const filterByPage = (vehicles, page) => {
  const keywords = {
    suv: ['suv', 'ertiga', 'rumion', 'innova', 'hycross', 'hybrid', 'fortuner', 'carens'],
    tempo: ['tempo', 'traveller'],
    urbania: ['urbania'],
  }[page] || [];

  return vehicles.filter((vehicle) => matchesAny(vehicle, keywords));
};

export const loadLandingPageVehicles = async (page, imageMap = {}) => {
  const vehicles = await fetchPublicVehicles();
  const byTrip = groupVehiclesByTrip(vehicles);

  return {
    airport: dedupeVehicles(filterByPage(byTrip.airport || [], page)).map((vehicle) => toLandingVehicle(vehicle, 'airport', imageMap)),
    outstation: dedupeVehicles(filterByPage(byTrip.outstation || [], page)).map((vehicle) => toLandingVehicle(vehicle, 'outstation', imageMap)),
    local: dedupeVehicles(filterByPage(byTrip.local || [], page)).map((vehicle) => toLandingVehicle(vehicle, 'local', imageMap)),
  };
};

export const buildLandingBookingState = (item, trip) => {
  const sourceVehicle = item.apiVehicle || item;
  const vehicleBackendId = getVehicleBackendId(sourceVehicle) || getVehicleBackendId(item);
  const vehicleData = {
    ...sourceVehicle,
    ...(vehicleBackendId
      ? {
          vehicleId: vehicleBackendId,
          backendVehicleId: vehicleBackendId,
        }
      : {}),
    category: sourceVehicle.category || sourceVehicle.type || trip,
  };
  const bookingData = buildBookingDataFromVehicle(vehicleData, trip);

  return {
    selectedVehicleData: vehicleData,
    initialRouteData: bookingData,
    tripType: toBookingTripLabel(trip),
  };
};
